import { useEffect, useState, useRef } from "react";
import { fmtDate, fmtDateTime, fmtH } from "../lib/format";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { fileUrl, API_BASE } from "../lib/api";
import { ArrowLeft, Upload, Trash2, Plus, Pencil, FileText, Image as ImageIcon, BookOpen, Download, Users, X, CloudSun, TrendingUp, Mic, MicOff, Receipt, Camera, ClipboardCheck, ScanLine, CheckCircle2 } from "lucide-react";
import { STATUS_OPTIONS, normalizeStatus } from "../lib/jobStatus";
import { enqueueDiary, flushDiaryQueue, getDiaryQueue, retryDiary, removeDiary, DIARY_QUEUE_EVENT } from "../lib/diaryQueue";
import { toast } from "sonner";
import { ClientPicker } from "../components/ClientPicker";
import { ClientField } from "../components/ClientField";
import HandoverPanel from "./HandoverPanel";
import { JobCraftsmen } from "../components/JobCraftsmen";
import { Fab } from "../components/Fab";
import { useAuth } from "../context/AuthContext";
import { isTech } from "../lib/roles";
import { JobBudgets } from "../components/JobBudgets";
import { Addendums } from "../components/addendums/Addendums";
import { BudgetVsActual } from "../components/BudgetVsActual";
import { MaterialConsumption } from "../components/MaterialConsumption";
import { JobDeliveryNotes } from "../components/InvoiceSplitEditor";
import { JobDocuments } from "../components/JobDocuments";
import { statusLabel } from "../lib/jobStatus";
import { PAY_MODES, PayModeBadge, payModeLabel } from "../lib/payMode";
import { VatModeSelect } from "../lib/vatMode";
import { JobHistory } from "../components/JobHistory";
import { useCostMatches, MatchBadge, CostMatchSuggestions } from "../components/CostInvoiceMatches";

const EMPTY = {
    title: "", client_id: null, client_name: "", client_address: "", client_phone: "", client_email: "",
    site_address: "", description: "", status: "poptavka", start_date: "", end_date: "", notes: "", planned_revenue: 0,
    tags: [], warranty_months: 0, warranty_start_date: "", pay_mode: "hodinova", labor_price_base: 0, default_vat_mode: ""
};

const SUGGESTED_TAGS = ["Koupelna", "Bazén", "Terasa", "Dlažba", "Obklady", "Sauna", "Wellness", "Schody"];

const fmt = (n) => (Number(n) || 0).toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " Kč";

export default function JobDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const tech = isTech(user);
    const isNew = id === "nova";
    const [editDiary, setEditDiary] = useState(null);
    const [photoSheet, setPhotoSheet] = useState(false);
    const focusForm = (testid) => {
        const el = document.querySelector(`[data-testid="${testid}"]`);
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
        setTimeout(() => el?.focus(), 350);
    };
    const triggerInput = (testid) => { setPhotoSheet(false); document.querySelector(`[data-testid="${testid}"]`)?.click(); };
    useEffect(() => {
        if (!photoSheet) return;
        const onKey = (e) => e.key === "Escape" && setPhotoSheet(false);
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [photoSheet]);
    const [job, setJob] = useState(isNew ? EMPTY : null);
    const [clients, setClients] = useState([]);
    const [newCost, setNewCost] = useState({ type: "material", description: "", quantity: 1, unit: "ks", unit_price: 0, supplier: "" });
    const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "info");
    const [showClientPicker, setShowClientPicker] = useState(false);
    const [profit, setProfit] = useState(null);
    const { data: matches, reload: reloadMatches } = useCostMatches(isNew || tech ? null : id);
    const [newDiary, setNewDiary] = useState({ date: new Date().toISOString().slice(0, 10), weather: "", temperature: "", workers: "", hours: 8, work_description: "", materials_used: "", notes: "" });
    const [scanBusy, setScanBusy] = useState(false);
    const [pendingReceipt, setPendingReceipt] = useState(null);
    const [diaryFiles, setDiaryFiles] = useState([]);
    const [diaryQueue, setDiaryQueue] = useState([]);
    const scanInputRef = useRef(null);

    const scanReceipt = async (file) => {
        if (!file) return;
        setScanBusy(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/ai/scan-receipt", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const amount = data.total_with_vat || data.total_without_vat || 0;
            setNewCost({
                type: data.type || "material",
                description: data.description || (data.supplier ? `Nákup ${data.supplier}` : ""),
                quantity: 1, unit: "ks", unit_price: amount,
                supplier: data.supplier || "", purchase_date: data.purchase_date || "",
                notes: data.vat_rate ? `DPH ${data.vat_rate} %${data.total_without_vat ? `, základ ${data.total_without_vat} Kč` : ""}` : "",
            });
            setPendingReceipt({ file, supplier: data.supplier, amount });
            toast.success("Účtenka přečtena — zkontrolujte údaje a klikněte Přidat");
        } catch (e) { toast.error(e.response?.data?.detail || "Načtení účtenky selhalo"); }
        finally { setScanBusy(false); }
    };

    useEffect(() => {
        if (!tech) api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
        if (isNew) return;
        api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => navigate("/zakazky"));
        if (!tech) api.get(`/jobs/${id}/profitability`).then((r) => setProfit(r.data)).catch(() => {});
    }, [id, isNew, navigate, tech]);

    const refreshDiaryQueue = () => getDiaryQueue(id).then(setDiaryQueue).catch(() => {});
    useEffect(() => {
        refreshDiaryQueue();
        const on = () => { refreshDiaryQueue(); };
        window.addEventListener(DIARY_QUEUE_EVENT, on);
        return () => window.removeEventListener(DIARY_QUEUE_EVENT, on);
    }, [id]); // eslint-disable-line
    useEffect(() => {
        const sentIds = diaryQueue.filter((q) => q.status === "sent").map((q) => q.serverId);
        if (sentIds.length && !sentIds.every((sid) => (job?.diary || []).some((e) => e.id === sid))) {
            api.get(`/jobs/${id}`).then((res) => setJob(res.data)).catch(() => {});
        }
    }, [diaryQueue]); // eslint-disable-line

    if (!job) return <div className="p-10 text-slate-500 uppercase text-xs tracking-widest">Načítání…</div>;

    const set = (k) => (e) => setJob({ ...job, [k]: e.target.value });

    const save = async () => {
        const payload = {
            title: job.title, client_id: job.client_id || null,
            client_name: job.client_name || "", client_address: job.client_address || "",
            client_phone: job.client_phone || "", client_email: job.client_email || "",
            site_address: job.site_address || "", description: job.description || "",
            status: job.status, start_date: job.start_date || null, end_date: job.end_date || null,
            notes: job.notes || "", planned_revenue: Number(job.planned_revenue) || 0,
            tags: job.tags || [],
            warranty_months: Number(job.warranty_months) || 0,
            warranty_start_date: job.warranty_start_date || null,
            pay_mode: job.pay_mode || "hodinova",
            labor_price_base: Number(job.labor_price_base) || 0,
            default_vat_mode: job.default_vat_mode || "",
            is_test: !!job.is_test,
        };
        try {
            if (isNew) {
                const { data } = await api.post("/jobs", payload);
                toast.success("Zakázka vytvořena");
                navigate(`/zakazky/${data.id}`);
            } else {
                const { data } = await api.put(`/jobs/${id}`, payload);
                setJob(data);
                const { data: p } = await api.get(`/jobs/${id}/profitability`);
                setProfit(p);
                toast.success("Uloženo");
            }
        } catch (e) { toast.error(e.response?.data?.detail || "Uložení selhalo"); }
    };
    const reloadJob = () => { api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => {}); if (!tech) api.get(`/jobs/${id}/profitability`).then((r) => setProfit(r.data)).catch(() => {}); };

    const del = async () => {
        try {
            const { data: l } = await api.get(`/jobs/${id}/links`);
            if (l.total > 0) {
                const lines = l.links.map((x) => `• ${x.label}: ${x.count}${x.mode === "unlink" ? " (zůstanou, jen se odpojí)" : " (smažou se)"}`).join("\n");
                if (!window.confirm(`Zakázka „${job.title}“ má navázané záznamy (${l.total}):\n\n${lines}\n\nSmazat i navázané záznamy? Tuto akci nelze vrátit.\n\nOK = Smazat i navázané záznamy · Zrušit = nic nemazat`)) return;
                const { data } = await api.delete(`/jobs/${id}`, { params: { cascade: true } });
                toast.success(`Zakázka smazána · ${Object.entries(data.deleted || {}).map(([k, v]) => `${k} ${v}`).join(", ") || "bez navázaných záznamů"}`);
            } else {
                if (!window.confirm("Smazat tuto zakázku?")) return;
                await api.delete(`/jobs/${id}`);
                toast.success("Zakázka smazána");
            }
            navigate("/zakazky");
        } catch (e) {
            const det = e.response?.data?.detail;
            toast.error(typeof det === "string" ? det : det?.message || "Smazání selhalo");
        }
    };

    const addCost = async () => {
        if (!newCost.description) { toast.error("Vyplňte popis"); return; }
        const { data } = await api.post(`/jobs/${id}/costs`, {
            ...newCost, quantity: Number(newCost.quantity), unit_price: Number(newCost.unit_price)
        });
        if (pendingReceipt?.file && data?.id) {
            try {
                const fd = new FormData();
                fd.append("file", pendingReceipt.file);
                await api.post(`/jobs/${id}/costs/${data.id}/receipt`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                const { data: fresh } = await api.get(`/jobs/${id}`);
                setJob(fresh);
                setNewCost({ type: "material", description: "", quantity: 1, unit: "ks", unit_price: 0, supplier: "", purchase_date: "", notes: "" });
                setPendingReceipt(null);
                toast.success("Náklad přidán včetně účtenky");
                return;
            } catch { toast.warning("Náklad uložen, účtenku se nepodařilo přiložit"); }
        }
        setJob({ ...job, costs: [...(job.costs || []), data] });
        if (!tech) { api.get(`/jobs/${id}/profitability`).then((r) => setProfit(r.data)).catch(() => {}); reloadMatches(); }
        setNewCost({ type: "material", description: "", quantity: 1, unit: "ks", unit_price: 0, supplier: "", purchase_date: "", notes: "" });
        setPendingReceipt(null);
        toast.success("Náklad přidán");
    };

    const delCost = async (cid) => {
        await api.delete(`/jobs/${id}/costs/${cid}`);
        setJob({ ...job, costs: (job.costs || []).filter((c) => c.id !== cid) });
        if (!tech) api.get(`/jobs/${id}/profitability`).then((r) => setProfit(r.data)).catch(() => {});
    };

    const uploadFile = async (kind, file, category) => {
        const fd = new FormData();
        fd.append("file", file);
        try {
            const url = kind === "photos" && category
                ? `/jobs/${id}/${kind}?category=${encodeURIComponent(category)}`
                : `/jobs/${id}/${kind}`;
            const { data } = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
            setJob({ ...job, [kind]: [...(job[kind] || []), data] });
            toast.success("Soubor nahrán");
        } catch (e) { toast.error(e.response?.data?.detail || "Nahrání selhalo"); }
    };

    const addDiary = async () => {
        if (!newDiary.work_description) { toast.error("Popište provedené práce"); return; }
        const payload = { ...newDiary, hours: Number(newDiary.hours) };
        const files = diaryFiles;
        setNewDiary({ date: new Date().toISOString().slice(0, 10), weather: "", temperature: "", workers: "", hours: 8, work_description: "", materials_used: "", notes: "" });
        setDiaryFiles([]);
        try {
            await enqueueDiary(id, payload, files);
        } catch {
            toast.error("Záznam se nepodařilo uložit do zařízení");
            setNewDiary(newDiary); setDiaryFiles(files);
            return;
        }
        if (!navigator.onLine) { toast.info("Jste offline — záznam čeká na odeslání"); return; }
        const r = await flushDiaryQueue();
        if (r.sent > 0) {
            toast.success(files.length ? `Záznam odeslán včetně ${files.length} fotek` : "Záznam odeslán");
            api.get(`/jobs/${id}`).then((res) => setJob(res.data)).catch(() => {});
        } else if (r.failed > 0) toast.error("Server záznam odmítl — viz stav u záznamu");
        else toast.info("Záznam čeká na odeslání");
    };

    const delDiary = async (eid) => {
        try {
            await api.delete(`/jobs/${id}/diary/${eid}`);
            setJob({ ...job, diary: (job.diary || []).filter((e) => e.id !== eid) });
        } catch (e) { toast.error(e.response?.data?.detail || "Smazání selhalo"); }
    };
    const canEditDiary = (e) => !tech || e.created_by === user?.id;
    const saveDiaryEdit = async () => {
        try {
            const { data } = await api.put(`/jobs/${id}/diary/${editDiary.id}`, {
                date: editDiary.date, weather: editDiary.weather || "", temperature: editDiary.temperature || "", workers: editDiary.workers || "",
                hours: Number(editDiary.hours) || 0, work_description: editDiary.work_description || "", materials_used: editDiary.materials_used || "", notes: editDiary.notes || "",
            });
            setJob({ ...job, diary: (job.diary || []).map((e) => (e.id === data.id ? data : e)) });
            setEditDiary(null); toast.success("Záznam upraven");
        } catch (e) { toast.error(e.response?.data?.detail || "Úprava selhala"); }
    };

    const openProtocol = () => {
        if (isNew) return;
        window.open(`${API_BASE}/jobs/${id}/protocol-pdf`, "_blank");
    };

    const pickClient = (c) => {
        setJob({
            ...job, client_id: c.id, client_name: c.name,
            client_address: [c.address, [c.zip, c.city].filter(Boolean).join(" ")].filter(Boolean).join(", "),
            client_phone: c.phone || "", client_email: c.email || ""
        });
        setShowClientPicker(false);
    };

    const totalCosts = (job.costs || []).reduce((s, c) => s + (c.invoice_match ? 0 : (c.total || c.quantity * c.unit_price || 0)), 0);
    const costsByType = (job.costs || []).reduce((acc, c) => { if (!c.invoice_match) acc[c.type] = (acc[c.type] || 0) + (c.total || c.quantity * c.unit_price || 0); return acc; }, {});
    const cbt = profit?.costs_by_type || costsByType;

    return (
        <div className="px-10 py-10 max-w-6xl mx-auto animate-fade-in" data-testid="job-detail-page">
            <Link to="/zakazky" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 hover:text-orange-600 mb-6">
                <ArrowLeft className="w-3 h-3" /> Zpět na zakázky
            </Link>

            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow mb-2">{isNew ? "Nová zakázka" : "Detail zakázky"}</div>
                    <h1 className="font-display text-4xl font-black tracking-tighter text-slate-900">{job.title || "Nová zakázka"}</h1>
                    {!isNew && <div className="mt-2 flex items-center gap-2 flex-wrap"><PayModeBadge mode={job.pay_mode} testid="job-pay-mode-badge" />{job.is_test && <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border border-fuchsia-400 bg-fuchsia-50 text-fuchsia-800" data-testid="job-test-badge">Testovací</span>}</div>}
                </div>
                {!tech && <div className="flex gap-2 flex-wrap">
                    {!isNew && (
                        <>
                            <button onClick={openProtocol} data-testid="print-protocol"
                                    className="px-4 py-2 text-xs uppercase tracking-wider font-semibold bg-slate-900 hover:bg-slate-800 text-white rounded-sm inline-flex items-center gap-1.5">
                                <Download className="w-3.5 h-3.5" /> Předávací protokol
                            </button>
                            <button onClick={del} data-testid="delete-job" className="px-4 py-2 text-xs uppercase tracking-wider border border-red-300 text-red-700 hover:bg-red-50 rounded-sm">Smazat</button>
                        </>
                    )}
                    <button onClick={save} data-testid="save-job" className="px-5 py-2 text-xs uppercase tracking-wider font-semibold bg-orange-600 hover:bg-orange-700 text-white rounded-sm">
                        {isNew ? "Vytvořit" : "Uložit změny"}
                    </button>
                </div>}
            </div>

            <div className="border-b border-slate-200 mb-6 flex gap-1 overflow-x-auto">
                {[
                    { k: "info", l: "Informace" },
                    !isNew && !tech && { k: "profit", l: "Zisk / P&L" },
                    !isNew && !tech && { k: "bva", l: "Plán vs. skutečnost" },
                    !isNew && { k: "addendums", l: "Dodatky" },
                    !isNew && !tech && { k: "costs", l: `Náklady (${(job.costs || []).length})` },
                    !isNew && { k: "craftsmen", l: "Řemeslníci" },
                    !isNew && { k: "budgets", l: tech ? "Rozpočty" : "Rozpočty a smlouvy" },
                    !isNew && { k: "diary", l: `Deník (${(job.diary || []).length})` },
                    !isNew && { k: "photos", l: `Fotky (${(job.photos || []).length})` },
                    !isNew && !tech && { k: "docs", l: `Dokumenty (${(job.documents || []).length})` },
                    !isNew && !tech && { k: "handover", l: "Předání & Záruka" },
                    !isNew && !tech && { k: "history", l: "Historie" },
                ].filter(Boolean).map((t) => (
                    <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
                            className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 whitespace-nowrap ${
                                tab === t.k ? "border-orange-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"
                            }`}>
                        {t.l}
                    </button>
                ))}
            </div>

            {tab === "info" && tech && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="job-info-readonly">
                    <Card title="Zakázka">
                        <RO l="Název" v={job.title} />
                        <RO l="Fáze" v={statusLabel(job.status)} />
                        <RO l="Způsob odměny" v={payModeLabel(job.pay_mode)} />
                        <RO l="Termín" v={[job.start_date, job.end_date].filter(Boolean).join(" → ") || "—"} />
                        <RO l="Popis prací" v={job.description || "—"} pre />
                    </Card>
                    <Card title="Místo a kontakt">
                        <RO l="Místo stavby" v={job.site_address || "—"} />
                        <RO l="Kontakt na místě" v={job.client_name || "—"} />
                        <RO l="Telefon" v={job.client_phone ? <a href={`tel:${job.client_phone}`} className="text-orange-700">{job.client_phone}</a> : "—"} />
                        <RO l="Adresa klienta" v={job.client_address || "—"} />
                    </Card>
                </div>
            )}

            {tab === "craftsmen" && !isNew && <JobCraftsmen jobId={id} jobTitle={job.title} job={job} onJobChanged={reloadJob} />}
            {tab === "budgets" && !isNew && <><JobBudgets jobId={id} readOnly={tech} />{!tech && <div className="mt-6"><JobDocuments jobId={id} /></div>}</>}

            {tab === "info" && !tech && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card title="Základní údaje">
                        <Field label="Název zakázky *" v={job.title} on={set("title")} testid="job-title" />
                        <FieldSelect label="Stav (fáze)" v={normalizeStatus(job.status)} on={set("status")} testid="job-status" options={STATUS_OPTIONS} />
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Začátek" type="date" v={job.start_date || ""} on={set("start_date")} testid="job-start" />
                            <Field label="Konec" type="date" v={job.end_date || ""} on={set("end_date")} testid="job-end" />
                        </div>
                        <Field label="Plánovaný příjem (Kč, bez DPH)" type="number" v={job.planned_revenue || 0} on={set("planned_revenue")} testid="job-planned-revenue" />
                        <div>
                            <label className="label-eyebrow block mb-1.5">Způsob odměny řemeslníků</label>
                            <select value={job.pay_mode || "hodinova"} onChange={set("pay_mode")} disabled={job.pay_mode_locked} data-testid="job-pay-mode"
                                    className="w-full px-3 py-2 border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 rounded-sm disabled:opacity-60">
                                {PAY_MODES.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
                            </select>
                            <div className="text-[11px] text-slate-500 mt-1" data-testid="job-pay-mode-hint">
                                {job.pay_mode_locked ? "Nelze změnit — u zakázky už jsou vyplacené splátky." : job.pay_mode === "na_klic" ? "Dohodnutá částka za dílo + platební kalendář (záložka Řemeslníci)." : "Hodiny × hodinová sazba řemeslníka."}
                            </div>
                        </div>
                        {job.pay_mode === "na_klic" && (
                            <Field label="Cena práce v rozpočtu (Kč, bez DPH) — základ pro % odměny" type="number" v={job.labor_price_base || 0} on={set("labor_price_base")} testid="job-labor-base" />
                        )}
                        <div>
                            <label className="label-eyebrow block mb-1.5">Výchozí režim DPH pro fakturaci</label>
                            <VatModeSelect value={job.default_vat_mode || ""} onChange={(v) => setJob({ ...job, default_vat_mode: v })} allowAuto testid="job-default-vat-mode" />
                        </div>
                        <label className={`flex items-start gap-3 p-3 border cursor-pointer ${job.is_test ? "border-fuchsia-400 bg-fuchsia-50" : "border-slate-200"}`} data-testid="job-is-test-label">
                            <input type="checkbox" checked={!!job.is_test} onChange={(e) => setJob({ ...job, is_test: e.target.checked })} className="mt-0.5 w-4 h-4 accent-fuchsia-700" data-testid="job-is-test" />
                            <span><span className="text-sm font-semibold text-slate-900">Testovací zakázka</span><span className="block text-xs text-slate-600">Školení, zkoušky. Zakázka ani její záznamy se nepočítají do přehledů, výplat, DPH ani exportů pro účetní.</span></span>
                        </label>
                        <div>
                            <label className="label-eyebrow block mb-1.5">Tagy</label>
                            <TagsInput tags={job.tags || []} onChange={(tags) => setJob({ ...job, tags })} suggestions={SUGGESTED_TAGS} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Záruka (měsíce)" type="number" v={job.warranty_months || 0} on={set("warranty_months")} testid="job-warranty-months" />
                            <Field label="Záruka od (datum)" type="date" v={job.warranty_start_date || ""} on={set("warranty_start_date")} />
                        </div>
                        <FieldArea label="Popis prací" v={job.description} on={set("description")} testid="job-desc" />
                    </Card>

                    <Card title="Klient">
                        <ClientField label="Klient" value={job.client_name} linked={!!job.client_id} onOpen={() => setShowClientPicker(true)} testid="job-client" />
                        <Field label="Adresa klienta" v={job.client_address} on={set("client_address")} />
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Telefon" v={job.client_phone} on={set("client_phone")} />
                            <Field label="Email" type="email" v={job.client_email} on={set("client_email")} />
                        </div>
                        <Field label="Místo stavby" v={job.site_address} on={set("site_address")} testid="job-site" />
                    </Card>

                    <div className="md:col-span-2">
                        <Card title="Poznámky">
                            <FieldArea label="Interní poznámky" v={job.notes} on={set("notes")} testid="job-notes" rows={4} />
                        </Card>
                    </div>
                </div>
            )}

            {tab === "profit" && !isNew && profit && <ProfitabilityPanel p={profit} />}
            {tab === "bva" && !isNew && !tech && <div className="space-y-5"><BudgetVsActual job={job} /><MaterialConsumption jobId={job.id} /></div>}
            {tab === "addendums" && !isNew && <Addendums job={job} money={!tech} onChanged={reloadJob} />}

            {tab === "costs" && !isNew && (
                <div>
                    <div className="mb-6"><JobDeliveryNotes jobId={job.id} onChanged={reloadJob} /></div>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
                        <StatBlock label="Materiál" value={fmt(cbt.material || 0)} sub={profit?.received_costs?.material ? `z toho faktury ${fmt(profit.received_costs.material)}` : undefined} />
                        <StatBlock label="Práce" value={fmt(cbt.prace || 0)} sub={profit?.received_costs?.prace ? `z toho faktury ${fmt(profit.received_costs.prace)}` : undefined} />
                        <StatBlock label="Doprava" value={fmt(cbt.doprava || 0)} />
                        <StatBlock label="Ostatní" value={fmt(cbt.ostatni || 0)} sub={profit?.received_costs?.ostatni ? `z toho faktury ${fmt(profit.received_costs.ostatni)}` : undefined} />
                        <StatBlock label="Nářadí / půjčovna" value={fmt(cbt.naradi || 0)} sub={profit?.received_costs?.naradi ? `z toho faktury ${fmt(profit.received_costs.naradi)}` : undefined} />
                        <StatBlock label="Řemeslníci" value={fmt((profit?.craftsmen?.amount) || 0)} />
                        <StatBlock label="Celkem" value={fmt(profit ? profit.total_costs : totalCosts)} accent sub={profit?.costs_matched_count ? `${profit.costs_matched_count}× doloženo fakturou (nepočítá se dvakrát)` : undefined} />
                    </div>
                    <CostMatchSuggestions jobId={job.id} suggestions={matches.suggestions} onChanged={() => { reloadJob(); reloadMatches(); }} />

                    <div className="bg-white border border-slate-200">
                        <div className="px-6 py-4 border-b-2 border-slate-900 flex items-center justify-between">
                            <div className="font-display font-bold text-slate-900 uppercase tracking-wider text-sm">Položky nákladů</div>
                            <div className="text-[10px] text-slate-500 uppercase tracking-widest">Řemeslníci se evidují v sekci <Link to="/remeslnici" className="text-orange-700 hover:underline">Řemeslníci</Link></div>
                        </div>
                        <div className="p-4 bg-slate-50 border-b border-slate-200 space-y-2">
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <button onClick={() => scanInputRef.current?.click()} disabled={scanBusy} data-testid="scan-receipt-button"
                                        className="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wider border-2 border-amber-500 text-amber-800 bg-amber-50 hover:bg-amber-100 rounded-sm disabled:opacity-60">
                                    <ScanLine className={`w-4 h-4 ${scanBusy ? "animate-pulse" : ""}`} /> {scanBusy ? "AI čte účtenku…" : "Naskenovat účtenku (AI)"}
                                </button>
                                <input ref={scanInputRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="scan-receipt-input"
                                       onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; scanReceipt(f); }} />
                                {pendingReceipt ? (
                                    <div className="flex items-center gap-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-300 px-3 py-1.5 rounded-sm" data-testid="pending-receipt">
                                        <Receipt className="w-3.5 h-3.5" />
                                        Účtenka připravena{pendingReceipt.supplier ? ` · ${pendingReceipt.supplier}` : ""} — přiloží se po kliknutí na Přidat
                                        <button onClick={() => setPendingReceipt(null)} className="text-slate-500 hover:text-red-600" title="Zrušit"><X className="w-3.5 h-3.5" /></button>
                                    </div>
                                ) : (
                                    <div className="text-[10px] text-slate-500 uppercase tracking-widest">Vyfoťte účtenku — AI předvyplní dodavatele, částku a datum</div>
                                )}
                            </div>
                            <div className="grid grid-cols-12 gap-2 items-end">
                                <select value={newCost.type} onChange={(e) => setNewCost({ ...newCost, type: e.target.value })} data-testid="cost-type" className="col-span-2 px-3 py-2 border border-slate-300 text-sm rounded-sm">
                                    <option value="material">Materiál</option>
                                    <option value="prace">Práce</option>
                                    <option value="doprava">Doprava</option>
                                    <option value="ostatni">Ostatní</option>
                                </select>
                                <input placeholder="Popis položky" value={newCost.description} onChange={(e) => setNewCost({ ...newCost, description: e.target.value })} data-testid="cost-desc" className="col-span-4 px-3 py-2 border border-slate-300 text-sm rounded-sm" />
                                <input type="number" step="0.01" placeholder="Množ." value={newCost.quantity} onChange={(e) => setNewCost({ ...newCost, quantity: e.target.value })} data-testid="cost-qty" className="col-span-1 px-3 py-2 border border-slate-300 text-sm rounded-sm text-right tabular-data" />
                                <input placeholder="ks" value={newCost.unit} onChange={(e) => setNewCost({ ...newCost, unit: e.target.value })} className="col-span-1 px-3 py-2 border border-slate-300 text-sm rounded-sm" />
                                <input type="number" step="0.01" placeholder="Cena/MJ" value={newCost.unit_price} onChange={(e) => setNewCost({ ...newCost, unit_price: e.target.value })} data-testid="cost-price" className="col-span-2 px-3 py-2 border border-slate-300 text-sm rounded-sm text-right tabular-data" />
                                <button onClick={addCost} data-testid="add-cost" className="col-span-2 px-3 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold uppercase tracking-wider rounded-sm flex items-center justify-center gap-1">
                                    <Plus className="w-3 h-3" /> Přidat
                                </button>
                            </div>
                            <div className="grid grid-cols-12 gap-2 items-end">
                                <input placeholder="Dodavatel (Siko, Hornbach, ...)" value={newCost.supplier} onChange={(e) => setNewCost({ ...newCost, supplier: e.target.value })} data-testid="cost-supplier" className="col-span-6 px-3 py-2 border border-slate-300 text-sm rounded-sm" />
                                <input type="date" value={newCost.purchase_date || ""} onChange={(e) => setNewCost({ ...newCost, purchase_date: e.target.value })} data-testid="cost-purchase-date" className="col-span-3 px-3 py-2 border border-slate-300 text-sm rounded-sm" placeholder="Datum nákupu" />
                                <input placeholder="Poznámka" value={newCost.notes || ""} onChange={(e) => setNewCost({ ...newCost, notes: e.target.value })} className="col-span-3 px-3 py-2 border border-slate-300 text-sm rounded-sm" />
                            </div>
                        </div>

                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                                    <th className="px-4 py-2 font-semibold">Typ</th>
                                    <th className="px-4 py-2 font-semibold">Popis</th>
                                    <th className="px-4 py-2 font-semibold">Dodavatel</th>
                                    <th className="px-4 py-2 font-semibold text-right">Množ.</th>
                                    <th className="px-4 py-2 font-semibold">MJ</th>
                                    <th className="px-4 py-2 font-semibold text-right">Cena/MJ</th>
                                    <th className="px-4 py-2 font-semibold text-right">Celkem</th>
                                    <th className="px-4 py-2 font-semibold text-center">Účtenka</th>
                                    <th className="px-4 py-2"></th>
                                </tr>
                            </thead>
                            <tbody className="tabular-data">
                                {(job.costs || []).length === 0 && (
                                    <tr><td colSpan={9} className="px-6 py-8 text-center text-slate-400">Zatím žádné náklady.</td></tr>
                                )}
                                {(job.costs || []).map((c, i) => (
                                    <tr key={c.id} className={`border-b border-slate-100 ${i % 2 ? "bg-slate-50/40" : ""}`} data-testid={`cost-row-${c.id}`}>
                                        <td className="px-4 py-2 text-xs uppercase tracking-wider text-slate-600">{typeLabel(c.type)}</td>
                                        <td className="px-4 py-2">
                                            <div className={c.invoice_match ? "text-slate-500" : ""}>{c.description}</div>
                                            {c.purchase_date && <div className="text-[10px] text-slate-400">{c.purchase_date}</div>}
                                            <div className="mt-1"><MatchBadge cost={c} jobId={id} onChanged={() => { reloadJob(); reloadMatches(); }} /></div>
                                        </td>
                                        <td className="px-4 py-2 text-slate-600">{c.supplier || "—"}</td>
                                        <td className="px-4 py-2 text-right">{Number(c.quantity).toFixed(2).replace(".", ",")}</td>
                                        <td className="px-4 py-2 text-slate-600">{c.unit}</td>
                                        <td className="px-4 py-2 text-right">{fmt(c.unit_price)}</td>
                                        <td className={`px-4 py-2 text-right font-semibold ${c.invoice_match ? "line-through text-slate-400" : ""}`} title={c.invoice_match ? "Počítá se z přijaté faktury" : ""}>{fmt(c.total || c.quantity * c.unit_price)}</td>
                                        <td className="px-4 py-2 text-center">
                                            <ReceiptCell cost={c} jobId={id} onUpdated={() => api.get(`/jobs/${id}`).then((r) => setJob(r.data))} />
                                        </td>
                                        <td className="px-4 py-2 text-right">
                                            <button onClick={() => delCost(c.id)} data-testid={`delete-cost-${c.id}`} className="text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {tab === "diary" && !isNew && <Fab icon={Plus} label="Nový zápis" onClick={() => focusForm("diary-desc")} testid="fab-diary" />}
            {tab === "photos" && !isNew && <Fab icon={Camera} label="Přidat fotku" onClick={() => setPhotoSheet(true)} testid="fab-photo" />}
            {tab === "craftsmen" && !isNew && <Fab icon={Plus} label="Zapsat hodiny" onClick={() => focusForm("jc-card-select")} testid="fab-hours" />}
            {photoSheet && (
                <div className="fixed inset-0 z-[90] bg-slate-900/60 flex items-end md:hidden" onPointerDown={() => setPhotoSheet(false)} data-testid="photo-sheet">
                    <div className="w-full bg-white rounded-t-2xl p-4 pb-safe space-y-2" onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()} style={{ paddingBottom: "calc(var(--safe-bottom) + 16px)" }}>
                        <div className="h-1 w-10 bg-slate-300 rounded-full mx-auto mb-3"></div>
                        <button onClick={() => triggerInput("capture-photo")} data-testid="sheet-camera" className="w-full min-h-[52px] flex items-center gap-3 px-4 bg-orange-600 text-white font-semibold rounded-sm"><Camera className="w-5 h-5" /> Vyfotit fotoaparátem</button>
                        <button onClick={() => triggerInput("upload-photos")} data-testid="sheet-gallery" className="w-full min-h-[52px] flex items-center gap-3 px-4 border border-slate-300 text-slate-800 font-semibold rounded-sm"><ImageIcon className="w-5 h-5" /> Vybrat z galerie</button>
                        <button onClick={() => setPhotoSheet(false)} className="w-full min-h-[48px] text-slate-500 text-sm">Zrušit</button>
                    </div>
                </div>
            )}
            {tab === "diary" && !isNew && (
                <div className="space-y-4">
                    <div className="bg-white border border-slate-200">
                        <div className="px-6 py-4 border-b-2 border-slate-900">
                            <div className="font-display font-bold text-slate-900 uppercase tracking-wider text-sm">Nový záznam do deníku</div>
                        </div>
                        <div className="p-6 grid grid-cols-1 md:grid-cols-4 gap-3">
                            <Field label="Datum" type="date" v={newDiary.date} on={(e) => setNewDiary({ ...newDiary, date: e.target.value })} testid="diary-date" />
                            <Field label="Počasí" v={newDiary.weather} on={(e) => setNewDiary({ ...newDiary, weather: e.target.value })} testid="diary-weather" />
                            <Field label="Teplota (°C)" v={newDiary.temperature} on={(e) => setNewDiary({ ...newDiary, temperature: e.target.value })} />
                            <Field label="Odpracováno (hod)" type="number" v={newDiary.hours} on={(e) => setNewDiary({ ...newDiary, hours: e.target.value })} />
                            <div className="md:col-span-2">
                                <Field label="Pracovníci" v={newDiary.workers} on={(e) => setNewDiary({ ...newDiary, workers: e.target.value })} testid="diary-workers" />
                            </div>
                            <div className="md:col-span-2">
                                <Field label="Použitý materiál" v={newDiary.materials_used} on={(e) => setNewDiary({ ...newDiary, materials_used: e.target.value })} />
                            </div>
                            <div className="md:col-span-4">
                                <label className="label-eyebrow block mb-1.5">Popis prací *</label>
                                <textarea rows={3} value={newDiary.work_description} onChange={(e) => setNewDiary({ ...newDiary, work_description: e.target.value })} data-testid="diary-desc"
                                          className="w-full px-3 py-2 border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 rounded-sm resize-none" />
                            </div>
                            <div className="md:col-span-4 flex flex-wrap items-center justify-between gap-3">
                                <div className="flex items-center gap-3 flex-wrap">
                                    <label className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider border border-slate-300 hover:bg-slate-50 rounded-sm cursor-pointer" data-testid="diary-photos-label">
                                        <Camera className="w-3.5 h-3.5" /> Přidat fotky
                                        <input type="file" accept="image/*" multiple capture="environment" className="hidden" data-testid="diary-photos-input"
                                               onChange={(e) => { setDiaryFiles([...diaryFiles, ...Array.from(e.target.files || [])]); e.target.value = ""; }} />
                                    </label>
                                    {diaryFiles.map((f, i) => (
                                        <span key={i} className="inline-flex items-center gap-1 text-[11px] bg-slate-100 border border-slate-200 px-2 py-1 rounded-sm" data-testid={`diary-photo-chip-${i}`}>
                                            <ImageIcon className="w-3 h-3 text-slate-500" /> {f.name.length > 18 ? f.name.slice(0, 15) + "…" : f.name}
                                            <button onClick={() => setDiaryFiles(diaryFiles.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                                        </span>
                                    ))}
                                    {diaryFiles.length > 0 && <span className="text-[10px] text-slate-500 uppercase tracking-widest">fotky se zmenší na 1600 px</span>}
                                </div>
                                <button onClick={addDiary} data-testid="add-diary" className="px-5 py-2 bg-orange-600 hover:bg-orange-700 text-white text-xs uppercase tracking-wider font-semibold rounded-sm inline-flex items-center gap-1">
                                    <Plus className="w-3.5 h-3.5" /> Přidat záznam
                                </button>
                            </div>
                        </div>
                    </div>

                    {diaryQueue.filter((q) => q.status !== "sent" || !(job.diary || []).some((e) => e.id === q.serverId)).length > 0 && (
                        <div className="space-y-2" data-testid="diary-queue">
                            {diaryQueue.filter((q) => q.status !== "sent" || !(job.diary || []).some((e) => e.id === q.serverId)).slice().reverse().map((q) => (
                                <div key={q.localId} data-testid={`diary-queued-${q.localId}`}
                                     className={`bg-white border-l-4 p-4 border border-slate-200 ${q.status === "error" ? "border-l-red-600" : q.status === "sent" ? "border-l-emerald-500" : "border-l-amber-500"}`}>
                                    <div className="flex items-start justify-between gap-3 flex-wrap">
                                        <div className="flex items-center gap-3">
                                            <div className="font-display font-bold text-slate-900">{fmtDate(q.payload.date)}</div>
                                            <span data-testid={`diary-status-${q.localId}`} className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${q.status === "error" ? "bg-red-50 text-red-700 border-red-300" : q.status === "sent" ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-amber-50 text-amber-800 border-amber-300"}`}>
                                                {q.status === "error" ? "Chyba – zkusit znovu" : q.status === "sent" ? "Odesláno" : q.status === "sending" ? "Odesílám…" : "Čeká na odeslání"}
                                            </span>
                                            {q.photos?.length > 0 && <span className="text-xs text-slate-500 inline-flex items-center gap-1"><ImageIcon className="w-3 h-3" /> {q.photos.filter((p) => p.uploaded).length}/{q.photos.length} fotek</span>}
                                        </div>
                                        <div className="flex gap-2">
                                            {q.status === "error" && (
                                                <button onClick={() => retryDiary(q.localId)} data-testid={`diary-retry-${q.localId}`} className="text-xs px-2.5 py-1 border border-red-600 text-red-700 hover:bg-red-50 rounded-sm uppercase tracking-wider font-semibold">Zkusit znovu</button>
                                            )}
                                            {q.status !== "sending" && (
                                                <button onClick={() => removeDiary(q.localId)} className="text-slate-400 hover:text-red-600" title="Odebrat z fronty"><Trash2 className="w-3.5 h-3.5" /></button>
                                            )}
                                        </div>
                                    </div>
                                    <p className="text-sm text-slate-700 whitespace-pre-wrap mt-2">{q.payload.work_description}</p>
                                    {q.error && <div className="text-xs text-red-700 mt-1">{q.error}</div>}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Entries list */}
                    <div className="space-y-3">
                        {(job.diary || []).length === 0 && (
                            <div className="bg-white border border-slate-200 p-12 text-center text-slate-400">
                                <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                                Žádné záznamy v deníku.
                            </div>
                        )}
                        {(job.diary || []).slice().reverse().map((e) => (
                            <div key={e.id} className="bg-white border border-slate-200 p-5">
                                <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                        <div className="font-display font-bold text-slate-900">{fmtDate(e.date)}</div>
                                        {e.weather && <span className="inline-flex items-center gap-1 text-xs text-slate-500"><CloudSun className="w-3.5 h-3.5" />{e.weather}{e.temperature ? ` · ${e.temperature}°C` : ""}</span>}
                                        {e.hours > 0 && <span className="text-xs text-slate-500 tabular-data">{fmtH(e.hours)}</span>}
                                    </div>
                                    {canEditDiary(e) && <div className="flex gap-1">
                                        <button onClick={() => setEditDiary({ ...e })} data-testid={`diary-edit-${e.id}`} className="text-slate-400 hover:text-slate-900"><Pencil className="w-3.5 h-3.5" /></button>
                                        <button onClick={() => delDiary(e.id)} data-testid={`diary-delete-${e.id}`} className="text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                                    </div>}
                                </div>
                                {editDiary?.id === e.id && (
                                    <div className="mb-3 p-3 bg-amber-50 border border-amber-200 grid grid-cols-1 md:grid-cols-4 gap-2" data-testid="diary-edit-form">
                                        <Field label="Datum" type="date" v={editDiary.date} on={(ev) => setEditDiary({ ...editDiary, date: ev.target.value })} />
                                        <Field label="Počasí" v={editDiary.weather || ""} on={(ev) => setEditDiary({ ...editDiary, weather: ev.target.value })} />
                                        <Field label="Odpracováno (hod)" type="number" v={editDiary.hours || 0} on={(ev) => setEditDiary({ ...editDiary, hours: ev.target.value })} />
                                        <Field label="Pracovníci" v={editDiary.workers || ""} on={(ev) => setEditDiary({ ...editDiary, workers: ev.target.value })} />
                                        <div className="md:col-span-4">
                                            <label className="label-eyebrow block mb-1.5">Popis prací</label>
                                            <textarea rows={3} value={editDiary.work_description || ""} onChange={(ev) => setEditDiary({ ...editDiary, work_description: ev.target.value })} data-testid="diary-edit-desc"
                                                      className="w-full px-3 py-2 border border-slate-300 bg-white text-sm rounded-sm resize-none" />
                                        </div>
                                        <div className="md:col-span-4 flex gap-2 justify-end">
                                            <button onClick={() => setEditDiary(null)} className="px-3 py-1.5 text-xs uppercase tracking-wider border border-slate-300 rounded-sm">Zrušit</button>
                                            <button onClick={saveDiaryEdit} data-testid="diary-edit-save" className="px-3 py-1.5 text-xs uppercase tracking-wider font-semibold bg-orange-600 text-white rounded-sm">Uložit změny</button>
                                        </div>
                                    </div>
                                )}
                                {e.workers && <div className="text-xs text-slate-500 mb-2">Pracovníci: <span className="text-slate-700">{e.workers}</span></div>}
                                <p className="text-sm text-slate-700 whitespace-pre-wrap">{e.work_description}</p>
                                {e.materials_used && <div className="mt-2 text-xs text-slate-500">Materiál: <span className="text-slate-700">{e.materials_used}</span></div>}
                                {e.photos?.length > 0 && (
                                    <div className="mt-3 flex gap-2 flex-wrap" data-testid={`diary-photos-${e.id}`}>
                                        {e.photos.map((p) => (
                                            <a key={p.id} href={fileUrl(p.storage_path)} target="_blank" rel="noreferrer" className="block w-20 h-20 border border-slate-200 bg-slate-100 overflow-hidden hover:border-orange-400">
                                                <img src={fileUrl(p.storage_path)} alt={p.original_filename || "foto"} className="w-full h-full object-cover" loading="lazy" />
                                            </a>
                                        ))}
                                    </div>
                                )}
                                <div className="mt-3 flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-400">
                                    {e.created_by_name && <span>— {e.created_by_name}</span>}
                                    <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle2 className="w-3 h-3" /> Odesláno</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {tab === "photos" && !isNew && (
                <FilesPanel kind="photos" job={job} onUpload={(f) => uploadFile("photos", f)} accept="image/*" icon={ImageIcon} />
            )}
            {tab === "docs" && !isNew && (
                <FilesPanel kind="documents" job={job} onUpload={(f) => uploadFile("documents", f)} accept=".pdf,.doc,.docx,.xls,.xlsx" icon={FileText} />
            )}
            {tab === "handover" && !isNew && (
                <HandoverPanel job={job} onUpdate={() => api.get(`/jobs/${id}`).then((r) => setJob(r.data))} />
            )}
            {tab === "history" && !isNew && <JobHistory jobId={id} />}

            {showClientPicker && <ClientPicker clients={clients} onPick={pickClient} onClose={() => setShowClientPicker(false)} />}
        </div>
    );
}

function typeLabel(t) {
    return { material: "Materiál", prace: "Práce", doprava: "Doprava", ostatni: "Ostatní", naradi: "Nářadí / půjčovna", remeslnici: "Řemeslníci" }[t] || t;
}

function PlanVsActual({ pv, payMode, fmtP }) {
    const used = pv.used_pct ?? 0;
    const over = pv.diff < 0;
    const actualLabel = payMode === "na_klic" ? "Skutečnost (dohodnuto na klíč)" : "Skutečnost (odpracované hodiny × sazba)";
    return (
        <div className="bg-white border border-slate-200" data-testid="plan-vs-actual">
            <div className="px-6 py-3 border-b-2 border-slate-900 flex items-center justify-between gap-2 flex-wrap">
                <span className="font-display font-bold uppercase tracking-wider text-sm">Podíl řemeslníků – plán z rozpočtu vs. skutečnost</span>
                <span className="text-xs text-slate-500">{pv.basis === "schvalene" ? "schválené rozpočty" : "všechny rozpočty (žádný schválený)"} · {pv.budgets_count} · interní</span>
            </div>
            <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div><div className="text-[10px] font-bold uppercase tracking-widest mb-1 text-amber-700">Plán – podíl řemeslníků</div><div className="font-display text-xl font-black tabular-data text-amber-800" data-testid="pva-planned">{fmtP(pv.planned_craftsman)}</div><div className="text-xs text-slate-500 mt-0.5">{pv.planned_pct != null ? `${pv.planned_pct} % z práce ${fmtP(pv.planned_labor)}` : "—"}</div></div>
                <div><div className="text-[10px] font-bold uppercase tracking-widest mb-1 text-slate-500">{actualLabel}</div><div className="font-display text-xl font-black tabular-data text-slate-900" data-testid="pva-actual">{fmtP(pv.actual_craftsman)}</div><div className="text-xs text-slate-500 mt-0.5">{fmtH(pv.actual_hours)}</div></div>
                <div><div className="text-[10px] font-bold uppercase tracking-widest mb-1 text-slate-500">Vyplaceno řemeslníkům</div><div className="font-display text-xl font-black tabular-data text-emerald-700" data-testid="pva-paid">{fmtP(pv.actual_paid)}</div></div>
                <div><div className="text-[10px] font-bold uppercase tracking-widest mb-1 text-slate-500">{over ? "Překročení plánu" : "Rezerva oproti plánu"}</div><div className={`font-display text-xl font-black tabular-data ${over ? "text-red-700" : "text-emerald-700"}`} data-testid="pva-diff">{over ? "−" : ""}{fmtP(Math.abs(pv.diff))}</div><div className="text-xs text-slate-500 mt-0.5">plán firmy z práce {fmtP(pv.planned_company)}</div></div>
            </div>
            {pv.planned_craftsman > 0 && (
                <div className="px-6 pb-5">
                    <div className="flex items-center justify-between text-xs mb-1"><span className="label-eyebrow">Čerpání plánovaného podílu</span><span className="tabular-data font-semibold" data-testid="pva-used">{used.toFixed(1)} %</span></div>
                    <div className="h-3 bg-slate-100 relative overflow-hidden"><div className={`h-full ${used > 100 ? "bg-red-500" : used > 85 ? "bg-orange-500" : "bg-amber-400"}`} style={{ width: `${Math.min(100, used)}%` }}></div></div>
                </div>
            )}
        </div>
    );
}

function ProfitabilityPanel({ p }) {
    const fmtP = (n) => (Number(n) || 0).toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " Kč";
    const marginColor = p.margin >= 0 ? "text-green-700" : "text-red-700";
    const marginBg = p.margin >= 0 ? "bg-green-50 border-green-300" : "bg-red-50 border-red-300";
    const revenue = p.revenue || 0;
    const costPct = revenue > 0 ? Math.min(100, (p.total_costs / revenue) * 100) : 0;
    const types = Object.entries(p.costs_by_type || {});
    return (
        <div className="space-y-6" data-testid="job-profitability">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <StatBlockProfit label={{ fakturovano: "Příjem (fakturováno)", plan: "Příjem (plán + vícepráce)", rozpocet: "Příjem (rozpočet + vícepráce)" }[p.revenue_basis] || "Příjem"} value={fmtP(p.revenue)} extra={p.change_orders ? `vícepráce ${fmtP(p.change_orders)}` : undefined} />
                <StatBlockProfit label="Reálné náklady" value={fmtP(p.total_costs)} extra={p.received_costs?.total ? `z toho přijaté faktury ${fmtP(p.received_costs.total)}` : undefined} />
                {p.fully_invoiced ? (
                    <StatBlockProfit label="Zisk" value={fmtP(p.margin)} extra={p.margin_pct !== null ? `${p.margin_pct} %` : "—"} className={marginBg} valueClassName={marginColor} />
                ) : (
                    <StatBlockProfit label="Očekávaný zisk (podle plánu)" value={p.expected_profit == null ? "—" : fmtP(p.expected_profit)} extra={p.plan_basis ? `${p.plan_basis === "rozpocet" ? "rozpočet" : "plánovaný příjem"} ${fmtP(p.plan_revenue)} − náklady` : "bez plánu"} className={marginBg} valueClassName={marginColor} />
                )}
                {p.fully_invoiced ? (
                    <StatBlockProfit label="Vyfakturováno" value={fmtP(p.invoiced_subtotal)} extra={`Z toho zaplaceno ${fmtP(p.paid_subtotal)}`} />
                ) : (
                    <StatBlockProfit label="Skutečný zisk" value={fmtP(p.actual_profit)} extra={`vyfakturováno ${fmtP(p.invoiced_subtotal)} − náklady · zaplaceno ${fmtP(p.paid_subtotal)}`} valueClassName={p.actual_profit >= 0 ? "text-emerald-700" : "text-red-700"} />
                )}
            </div>

            {revenue > 0 && (
                <div className="bg-white border border-slate-200 p-6" data-testid="job-profit-bar">
                    <div className="flex items-center justify-between text-xs mb-2">
                        <span className="label-eyebrow">Náklady vs. příjem</span>
                        <span className="tabular-data text-slate-700 font-semibold">{costPct.toFixed(1)}% spotřebováno</span>
                    </div>
                    <div className="h-4 bg-slate-100 relative overflow-hidden">
                        <div className={`h-full ${costPct > 90 ? "bg-red-500" : costPct > 70 ? "bg-orange-500" : "bg-green-500"}`} style={{ width: `${costPct}%` }}></div>
                    </div>
                </div>
            )}

            {p.plan_vs_actual && p.plan_vs_actual.budgets_count > 0 && <PlanVsActual pv={p.plan_vs_actual} payMode={p.pay_mode} fmtP={fmtP} />}

            {p.pay_mode === "na_klic" && p.piecework && (
                <div className="bg-white border border-slate-200" data-testid="piecework-breakdown">
                    <div className="px-6 py-3 border-b-2 border-slate-900 font-display font-bold uppercase tracking-wider text-sm flex items-center gap-2">Řemeslníci — zakázka na klíč <PayModeBadge mode="na_klic" /></div>
                    <div className="p-6 grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-4">
                        <div><div className="text-[10px] font-bold uppercase tracking-widest mb-1 text-slate-500">Dohodnuto celkem</div><div className="font-display text-xl font-black tabular-data text-slate-900" data-testid="profit-pw-agreed">{fmtP(p.piecework.agreed)}</div><div className="text-[10px] text-slate-500 mt-0.5">aktivní dohody + vyúčtování ukončených + hodinově</div></div>
                        <div><div className="text-[10px] font-bold uppercase tracking-widest mb-1 text-slate-500">Vyplaceno (dohody)</div><div className="font-display text-xl font-black tabular-data text-emerald-700" data-testid="profit-pw-paid">{fmtP(p.piecework.paid)}</div></div>
                        <div><div className="text-[10px] font-bold uppercase tracking-widest mb-1 text-slate-500">Zbývá z dohod</div><div className={`font-display text-xl font-black tabular-data ${p.piecework.remaining > 0 ? "text-red-700" : "text-emerald-700"}`} data-testid="profit-pw-remaining">{fmtP(p.piecework.remaining)}</div></div>
                        <div><div className="text-[10px] font-bold uppercase tracking-widest mb-1 text-slate-500">Hodinově (hodiny × sazba)</div><div className="font-display text-xl font-black tabular-data text-slate-900" data-testid="profit-pw-hourly-cost">{fmtP(p.piecework.hourly_cost)}</div><div className="text-[10px] text-slate-500 mt-0.5">{fmtH(p.piecework.hourly_hours)} · jde do uzávěrky</div></div>
                        <div><div className="text-[10px] font-bold uppercase tracking-widest mb-1 text-slate-500">Zbývá z původního rozpočtu práce</div><div className={`font-display text-xl font-black tabular-data ${p.piecework.budget_remaining < 0 ? "text-red-700" : "text-emerald-700"}`} data-testid="profit-pw-budget-remaining">{p.piecework.budget_remaining < 0 ? `Přečerpáno o ${fmtP(Math.abs(p.piecework.budget_remaining))}` : fmtP(p.piecework.budget_remaining)}</div><div className="text-[10px] text-slate-500 mt-0.5">původně dohodnuto {fmtP(p.piecework.original)}</div></div>
                        <div><div className="text-[10px] font-bold uppercase tracking-widest mb-1 text-slate-500">Odpracováno</div><div className="font-display text-xl font-black tabular-data text-slate-900">{fmtH(p.piecework.hours)}</div></div>
                        <div><div className="text-[10px] font-bold uppercase tracking-widest mb-1 text-slate-500">Orientačně Kč/h</div><div className="font-display text-xl font-black tabular-data text-slate-900" data-testid="profit-pw-hourly">{p.piecework.hourly_equivalent != null ? fmtP(p.piecework.hourly_equivalent) : "—"}</div>
                            {p.piecework.hourly_cost_for_reference > 0 && <div className="text-xs text-slate-500 mt-0.5">hodinově by vyšlo {fmtP(p.piecework.hourly_cost_for_reference)}</div>}</div>
                    </div>
                </div>
            )}

            {p.pay_mode !== "na_klic" && p.craftsmen && p.craftsmen.entries_count > 0 && (
                <div className="bg-white border border-slate-200" data-testid="craftsmen-breakdown">
                    <div className="px-6 py-3 border-b-2 border-slate-900 font-display font-bold uppercase tracking-wider text-sm">Řemeslníci na zakázce</div>
                    <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-widest mb-1 text-slate-500">Odpracováno</div>
                            <div className="font-display text-xl font-black tabular-data text-slate-900">{fmtP(p.craftsmen.amount)}</div>
                            <div className="text-xs text-slate-500 mt-0.5">{fmtH(p.craftsmen.hours)} · {p.craftsmen.entries_count} záznamů</div>
                        </div>
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-widest mb-1 text-slate-500">Vyplaceno záloh</div>
                            <div className="font-display text-xl font-black tabular-data text-slate-900">{fmtP(p.craftsmen.advance_paid)}</div>
                        </div>
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-widest mb-1 text-slate-500">Zbývá doplatit</div>
                            <div className={`font-display text-xl font-black tabular-data ${p.craftsmen.balance_to_pay > 0 ? "text-red-700" : "text-emerald-700"}`}>
                                {fmtP(p.craftsmen.balance_to_pay)}
                            </div>
                        </div>
                        <div className="flex items-end">
                            <Link to="/remeslnici" className="text-xs uppercase tracking-widest font-bold text-orange-700 hover:underline">
                                Otevřít Řemeslníky →
                            </Link>
                        </div>
                    </div>
                </div>
            )}

            {types.length > 0 && (
                <div className="bg-white border border-slate-200">
                    <div className="px-6 py-3 border-b-2 border-slate-900 font-display font-bold uppercase tracking-wider text-sm">Struktura nákladů</div>
                    <div className="p-6 space-y-3">
                        {types.map(([t, v]) => {
                            const pct = p.total_costs > 0 ? (v / p.total_costs) * 100 : 0;
                            return (
                                <div key={t}>
                                    <div className="flex items-center justify-between text-xs mb-1">
                                        <span className="font-semibold uppercase tracking-wider text-slate-700">{typeLabel(t)}</span>
                                        <span className="tabular-data text-slate-700">{fmtP(v)} <span className="text-slate-400">· {pct.toFixed(0)}%</span></span>
                                    </div>
                                    <div className="h-2 bg-slate-100 overflow-hidden">
                                        <div className="h-full bg-orange-500" style={{ width: `${pct}%` }}></div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="bg-white border border-slate-200 px-6 py-4 flex items-center justify-between text-sm">
                <div className="text-slate-600">
                    <span className="font-semibold text-slate-900">{p.linked_budgets_count}</span> rozpočtů ·
                    <span className="font-semibold text-slate-900 ml-2">{p.linked_invoices_count}</span> faktur navázáno na tuto zakázku
                </div>
                <div className="text-xs text-slate-500">
                    Zdroj příjmu: {p.revenue_basis === "fakturovano" ? "z vystavených faktur (zakázka vyfakturovaná)" : p.plan_basis === "rozpocet" ? "z rozpočtu (má přednost před ručním plánem)" : p.plan_basis === "plan" ? "ručně zadaný plánovaný příjem" : "nezadán"}
                </div>
            </div>
        </div>
    );
}

function StatBlockProfit({ label, value, extra, className = "bg-white border-slate-200", valueClassName = "text-slate-900" }) {
    return (
        <div className={`border p-4 ${className}`}>
            <div className="text-[10px] font-bold uppercase tracking-widest mb-1 text-slate-500">{label}</div>
            <div className={`font-display text-xl font-black tabular-data ${valueClassName}`}>{value}</div>
            {extra && <div className="text-xs text-slate-500 mt-1 tabular-data">{extra}</div>}
        </div>
    );
}

function Card({ title, children }) {
    return (
        <div className="bg-white border border-slate-200 sharp-shadow">
            <div className="px-6 py-3 border-b-2 border-slate-900">
                <div className="font-display font-bold text-slate-900 text-sm uppercase tracking-wider">{title}</div>
            </div>
            <div className="p-6 space-y-4">{children}</div>
        </div>
    );
}

function Field({ label, v, on, type = "text", testid }) {
    return (
        <div className="flex-1">
            <label className="label-eyebrow block mb-1.5">{label}</label>
            <input type={type} value={v || ""} onChange={on} data-testid={testid}
                   className="w-full px-3 py-2 border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent rounded-sm" />
        </div>
    );
}
function FieldArea({ label, v, on, testid, rows = 3 }) {
    return (
        <div>
            <label className="label-eyebrow block mb-1.5">{label}</label>
            <textarea rows={rows} value={v || ""} onChange={on} data-testid={testid}
                      className="w-full px-3 py-2 border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent rounded-sm resize-none" />
        </div>
    );
}
function FieldSelect({ label, v, on, options, testid }) {
    return (
        <div>
            <label className="label-eyebrow block mb-1.5">{label}</label>
            <select value={v || ""} onChange={on} data-testid={testid}
                    className="w-full px-3 py-2 border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent rounded-sm">
                {options.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
            </select>
        </div>
    );
}
function StatBlock({ label, value, accent, sub }) {
    return (
        <div className={`border border-slate-200 p-4 ${accent ? "bg-slate-900 text-white" : "bg-white"}`}>
            <div className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${accent ? "text-orange-400" : "text-slate-500"}`}>{label}</div>
            <div className="font-display text-xl font-black tabular-data">{value}</div>
            {sub && <div className={`text-[10px] mt-1 ${accent ? "text-slate-300" : "text-slate-500"}`}>{sub}</div>}
        </div>
    );
}
function ReceiptCell({ cost, jobId, onUpdated }) {
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef(null);
    const hasReceipt = !!cost.receipt_url;
    const receiptDisplayUrl = hasReceipt
        ? `${API_BASE}${cost.receipt_url.replace(/^\/api/, "")}`
        : null;

    const handleUpload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post(`/jobs/${jobId}/costs/${cost.id}/receipt`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Účtenka nahrána");
            onUpdated();
        } catch (e) { toast.error(e.response?.data?.detail || "Nahrání selhalo"); }
        finally { setUploading(false); }
    };

    if (hasReceipt) {
        return (
            <div className="flex items-center justify-center gap-1">
                <a href={receiptDisplayUrl} target="_blank" rel="noreferrer" title="Zobrazit účtenku"
                   data-testid={`view-receipt-${cost.id}`}
                   className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900 font-semibold uppercase tracking-wider">
                    <Receipt className="w-3.5 h-3.5" /> ✓
                </a>
                <button onClick={() => inputRef.current?.click()} title="Nahradit" className="text-slate-400 hover:text-slate-700 text-xs">
                    <Upload className="w-3 h-3" />
                </button>
                <input ref={inputRef} type="file" accept="image/*,.pdf" className="hidden"
                       onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])} />
            </div>
        );
    }
    return (
        <div>
            <button onClick={() => inputRef.current?.click()} disabled={uploading}
                    data-testid={`upload-receipt-${cost.id}`}
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-slate-500 hover:text-orange-700 disabled:opacity-50">
                <Upload className="w-3 h-3" /> {uploading ? "..." : "Účtenka"}
            </button>
            <input ref={inputRef} type="file" accept="image/*,.pdf" capture="environment" className="hidden"
                   onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])} />
        </div>
    );
}

function FilesPanel({ kind, job, onUpload, accept, icon: Icon }) {
    const files = job[kind] || [];
    const isPhotos = kind === "photos";
    const [photoCategory, setPhotoCategory] = useState("obecne");
    const [filterCat, setFilterCat] = useState("all");
    const CATS = [
        { k: "pred", l: "Před", color: "bg-blue-100 text-blue-700 border-blue-300" },
        { k: "behem", l: "Během", color: "bg-orange-100 text-orange-700 border-orange-300" },
        { k: "po", l: "Po", color: "bg-emerald-100 text-emerald-700 border-emerald-300" },
        { k: "detail", l: "Detail", color: "bg-purple-100 text-purple-700 border-purple-300" },
        { k: "obecne", l: "Ostatní", color: "bg-slate-100 text-slate-700 border-slate-300" },
    ];
    const catInfo = (k) => CATS.find((c) => c.k === k) || CATS[4];
    const filteredFiles = isPhotos && filterCat !== "all"
        ? files.filter((f) => (f.category || "obecne") === filterCat)
        : files;

    return (
        <div>
            {isPhotos && (
                <div className="mb-4 flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mr-2">Kategorie pro upload:</span>
                    {CATS.map((c) => (
                        <button key={c.k} onClick={() => setPhotoCategory(c.k)} data-testid={`select-cat-${c.k}`}
                                className={`px-2.5 py-1 text-xs font-semibold uppercase tracking-wider border ${photoCategory === c.k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-900"} rounded-sm`}>
                            {c.l}
                        </button>
                    ))}
                </div>
            )}
            <div className={`grid ${isPhotos ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"} gap-3 mb-4`}>
                <label className="flex items-center justify-center gap-2 w-full p-8 border-2 border-dashed border-slate-300 bg-white hover:bg-slate-50 hover:border-orange-400 cursor-pointer rounded-sm">
                    <Upload className="w-5 h-5 text-slate-400" />
                    <span className="text-sm text-slate-600">Klikněte pro nahrání {isPhotos && `(${catInfo(photoCategory).l})`}</span>
                    <input type="file" accept={accept} className="hidden" data-testid={`upload-${kind}`}
                           onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0], isPhotos ? photoCategory : undefined)} />
                </label>
                {isPhotos && (
                    <label className="flex items-center justify-center gap-2 w-full p-8 border-2 border-dashed border-orange-400 bg-orange-50/40 hover:bg-orange-50 cursor-pointer rounded-sm">
                        <Camera className="w-5 h-5 text-orange-600" />
                        <span className="text-sm text-orange-800 font-semibold">Vyfotit teď ({catInfo(photoCategory).l})</span>
                        <input type="file" accept="image/*" capture="environment" className="hidden" data-testid="capture-photo"
                               onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0], photoCategory)} />
                    </label>
                )}
            </div>

            {isPhotos && files.length > 0 && (
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Filtrovat:</span>
                    <button onClick={() => setFilterCat("all")} data-testid="filter-cat-all"
                            className={`px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider border ${filterCat === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300"} rounded-sm`}>
                        Vše ({files.length})
                    </button>
                    {CATS.map((c) => {
                        const n = files.filter((f) => (f.category || "obecne") === c.k).length;
                        if (n === 0) return null;
                        return (
                            <button key={c.k} onClick={() => setFilterCat(c.k)} data-testid={`filter-cat-${c.k}`}
                                    className={`px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider border ${filterCat === c.k ? "bg-slate-900 text-white border-slate-900" : c.color} rounded-sm`}>
                                {c.l} ({n})
                            </button>
                        );
                    })}
                </div>
            )}

            {filteredFiles.length === 0 ? (
                <div className="text-center text-slate-400 py-12 bg-white border border-slate-200">Zatím nic nenahráno.</div>
            ) : (
                <div className={kind === "photos" ? "grid grid-cols-2 md:grid-cols-4 gap-3" : "space-y-2"}>
                    {filteredFiles.map((f) => kind === "photos" ? (
                        <a key={f.id} href={fileUrl(f.storage_path)} target="_blank" rel="noreferrer" className="block relative aspect-square border border-slate-200 bg-slate-100 overflow-hidden hover:border-orange-400 group"
                           data-testid={`photo-${f.id}`}>
                            <img src={fileUrl(f.storage_path)} alt={f.original_filename} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                            <span className={`absolute top-1.5 left-1.5 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border ${catInfo(f.category || "obecne").color}`}>
                                {catInfo(f.category || "obecne").l}
                            </span>
                        </a>
                    ) : (
                        <a key={f.id} href={fileUrl(f.storage_path)} target="_blank" rel="noreferrer" className="flex items-center gap-3 bg-white border border-slate-200 px-4 py-3 hover:border-orange-400 hover:bg-orange-50/30">
                            <Icon className="w-5 h-5 text-orange-600" />
                            <span className="text-sm text-slate-900 truncate flex-1">{f.original_filename}</span>
                            <span className="text-xs text-slate-400">{((f.size || 0) / 1024).toFixed(0)} KB</span>
                        </a>
                    ))}
                </div>
            )}
        </div>
    );
}

function TagsInput({ tags, onChange, suggestions }) {    const [input, setInput] = useState("");
    const add = (t) => { t = t.trim(); if (!t || tags.includes(t)) return; onChange([...tags, t]); setInput(""); };
    const remove = (t) => onChange(tags.filter((x) => x !== t));
    const available = suggestions.filter((s) => !tags.includes(s));
    return (
        <div data-testid="tags-input">
            <div className="flex flex-wrap gap-1.5 mb-2 min-h-[28px]">
                {tags.map((t) => (
                    <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-800 text-xs font-semibold border border-orange-300">
                        {t}
                        <button onClick={() => remove(t)} className="ml-1 hover:text-red-600">×</button>
                    </span>
                ))}
            </div>
            <div className="flex gap-2">
                <input value={input} onChange={(e) => setInput(e.target.value)}
                       onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(input); } }}
                       placeholder="Přidat tag a Enter…"
                       className="flex-1 px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 rounded-sm" />
                <button onClick={() => add(input)} className="px-3 py-2 text-xs uppercase tracking-wider border border-slate-300 hover:bg-slate-100 rounded-sm">+</button>
            </div>
            {available.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                    {available.map((s) => (
                        <button key={s} onClick={() => add(s)} className="px-2 py-0.5 text-xs border border-slate-200 text-slate-600 hover:border-orange-400 hover:text-orange-700">
                            + {s}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function RO({ l, v, pre }) {
    return (
        <div>
            <div className="label-eyebrow mb-1">{l}</div>
            <div className={`text-sm text-slate-900 ${pre ? "whitespace-pre-wrap" : ""}`}>{v}</div>
        </div>
    );
}
